import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    Smartphone,
    UploadCloud,
    MessageSquareText,
    Check,
    CheckCircle2,
    Play,
    Maximize2,
    ArrowRight,
    LayoutDashboard,
} from "lucide-react";
import { NeonBackground } from "@/components/reelflo/NeonBackground";
import { NeonLogo } from "@/components/reelflo/NeonLogo";
import { NeonButton } from "@/components/reelflo/NeonButton";
import { AnimatedBorderCard } from "@/components/reelflo/AnimatedBorderCard";
import { AuthModal } from "@/components/reelflo/AuthModal";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "/hero-creator.jpg";

const fadeUp = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.25 },
    transition: { duration: 0.6, ease: [0.21, 0.61, 0.35, 1] },
};

const STEPS = [
    {
        n: "1",
        icon: Smartphone,
        title: "Shoot your video.",
        desc: "Talk to your phone.",
    },
    {
        n: "2",
        icon: UploadCloud,
        title: "Upload it to ReelFlo",
        desc: "Send it to the portal.",
    },
    {
        n: "3",
        icon: MessageSquareText,
        title: "Get the ‘ready’ text",
        desc: "Your video is done.",
    },
];

const READY_CHIPS = [
    "Captions added",
    "Pauses cut",
    "Audio enhanced",
    "Music added",
    "Ready-to-post caption",
    "Transcript ready",
];

const PRICE_LEFT = [
    "Video edited",
    "Captions added",
    "Pauses removed",
    "Audio cleaned",
];
const PRICE_RIGHT = [
    "Music added",
    "Transcript included",
    "Ready-to-post copy",
];

const PhoneMockup = ({ className = "" }) => (
    <div
        className={`overflow-hidden rounded-[30px] border-2 border-white/15 bg-black ${className}`}
    >
        <div className="relative">
            <img
                src={HERO_IMG}
                alt="ReelFlo finished video preview"
                className="aspect-[9/16] w-full object-cover object-[60%_18%]"
            />
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, transparent 30%, rgba(0,0,0,0.55) 100%)",
                }}
            />
            {/* caption overlay — media preview of the finished product */}
            <div className="absolute inset-x-0 top-[34%] px-4">
                <p className="rf-heading text-2xl font-extrabold leading-[0.95] text-white drop-shadow-lg">
                    STOP
                    <br />
                    SCROLLING.
                    <br />
                    <span className="text-cyan-400">LISTEN UP.</span>
                </p>
                <p className="mt-2 max-w-[80%] text-[13px] font-medium leading-snug text-white/90 drop-shadow">
                    This one change completely changed my content.
                </p>
            </div>
            {/* player bar — media preview */}
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 px-3 pb-3 pt-6">
                <Play className="h-4 w-4 fill-white text-white" />
                <span className="text-[11px] font-medium text-white/90">
                    0:02 / 0:45
                </span>
                <div className="relative mx-1 h-1 flex-1 rounded-full bg-white/30">
                    <div className="absolute left-0 top-0 h-full w-[16%] rounded-full bg-gradient-to-r from-purple-400 to-cyan-400" />
                    <div className="absolute left-[16%] top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
                </div>
                <Maximize2 className="h-4 w-4 text-white/90" />
            </div>
        </div>
    </div>
);

export default function Landing() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState("signup");

    const openAuth = (m) => {
        if (isAuthenticated && m === "signup") {
            navigate("/dashboard");
            return;
        }
        setMode(m);
        setModalOpen(true);
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-black text-white">
            <NeonBackground />

            {/* Header (transparent, over hero) */}
            <header className="absolute inset-x-0 top-0 z-50">
                <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
                    <NeonLogo onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
                    <div className="flex items-center gap-2 sm:gap-4">
                        {isAuthenticated ? (
                            <NeonButton
                                size="sm"
                                pulse={false}
                                data-testid="site-header-dashboard-button"
                                onClick={() => navigate("/dashboard")}
                            >
                                <LayoutDashboard className="h-4 w-4" /> Dashboard
                            </NeonButton>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    data-testid="open-login-modal-button"
                                    onClick={() => openAuth("login")}
                                    className="inline-flex min-h-11 items-center rounded-md px-3 text-base font-medium text-white/85 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
                                >
                                    Log in
                                </button>
                                <NeonButton
                                    size="sm"
                                    pulse={false}
                                    data-testid="site-header-signup-button"
                                    onClick={() => openAuth("signup")}
                                >
                                    Sign Up
                                </NeonButton>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative min-h-[600px] lg:min-h-[720px]">
                {/* desktop full-bleed image on the right */}
                <div className="absolute inset-y-0 right-0 hidden w-[60%] lg:block">
                    <img
                        src={HERO_IMG}
                        alt="Creator filming herself on her phone in a neon city"
                        className="h-full w-full object-cover object-[55%_22%]"
                    />
                    <div
                        className="pointer-events-none absolute inset-0"
                        style={{
                            background:
                                "linear-gradient(90deg, #000 0%, rgba(0,0,0,0.65) 22%, transparent 55%), linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 25%, transparent 70%, #000 100%)",
                        }}
                    />
                </div>

                {/* phone mockup (desktop) */}
                <div className="absolute bottom-16 right-6 z-20 hidden w-[230px] lg:block xl:right-16">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.3 }}
                    >
                        <PhoneMockup />
                    </motion.div>
                </div>

                <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 px-5 sm:px-8 lg:grid-cols-2">
                    {/* Left text */}
                    <div className="flex flex-col justify-center pb-6 pt-20 lg:pb-24 lg:pt-32">
                        <motion.h1
                            data-testid="hero-headline"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.21, 0.61, 0.35, 1] }}
                            className="rf-display max-w-xl text-4xl uppercase leading-[1.02] text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.65)] sm:text-6xl lg:text-7xl"
                        >
                            Make your video.
                            <br />
                            <span className="text-cyan-300">We do the rest.</span>
                        </motion.h1>

                        <motion.p
                            data-testid="hero-subheadline"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.12 }}
                            className="mt-5 max-w-md text-base leading-relaxed text-white/70 sm:text-lg"
                        >
                            Upload it and we edit it, add captions, clean the
                            audio, cut the pauses, add music, and send it back
                            ready to post.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.24 }}
                            className="mt-7 flex flex-col gap-3"
                        >
                            <NeonButton
                                size="lg"
                                data-testid="hero-signup-button"
                                onClick={() => openAuth("signup")}
                                className="group w-full max-w-md sm:min-w-[360px]"
                            >
                                Sign Up
                                <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                            </NeonButton>
                            <p className="inline-flex items-center gap-2 text-base font-semibold text-white/90">
                                <Check className="h-5 w-5 flex-none text-cyan-300" />
                                Edited. Captioned. Cleaned up. Ready to post.
                            </p>
                        </motion.div>
                    </div>

                    {/* Mobile image + phone */}
                    <div className="relative mb-5 lg:hidden">
                        <div className="relative overflow-hidden rounded-lg border border-white/12">
                            <img
                                src={HERO_IMG}
                                alt="Creator filming herself on her phone"
                                className="aspect-video w-full object-cover object-[55%_20%]"
                            />
                            <div
                                className="pointer-events-none absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.5) 100%)",
                                }}
                            />
                        </div>
                        <div className="absolute -bottom-4 right-3 w-28">
                            <PhoneMockup />
                        </div>
                    </div>
                </div>
            </section>

            <main className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8">
                {/* How it works — quiet rhythm section between hero and pricing */}
                <motion.section
                    {...fadeUp}
                    data-testid="how-it-works-section"
                    className="border-t border-white/10 py-14"
                >
                    <h2 className="rf-heading text-lg font-semibold text-white/60">
                        How it works
                    </h2>
                    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                        {STEPS.map(({ n, icon: Icon, title, desc }) => (
                            <div
                                key={n}
                                data-testid={`how-it-works-step-${n}`}
                                className="flex items-start gap-4 rounded-lg border border-white/10 bg-surface-panel p-5"
                            >
                                <Icon className="h-5 w-5 flex-none text-purple-300" />
                                <div>
                                    <p className="text-base font-semibold text-white">
                                        {title}
                                    </p>
                                    <p className="mt-1 text-sm text-white/55">
                                        {desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.section>

                {/* Pricing — the page's strongest visual destination. */}
                <motion.section
                    {...fadeUp}
                    data-testid="pricing-section"
                    className="pb-24"
                >
                    <AnimatedBorderCard data-testid="pricing-card">
                        <div className="p-8 sm:p-12">
                            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-16">
                                {/* Left: price */}
                                <div className="flex flex-col">
                                    <h2 className="rf-heading text-2xl font-bold leading-tight sm:text-3xl">
                                        Everything done for you.
                                    </h2>
                                    <span
                                        aria-hidden="true"
                                        className="mt-4 h-1 w-16 rounded-full bg-purple-500"
                                    />
                                    <div
                                        data-testid="public-price"
                                        className="mt-7 flex items-end gap-2.5"
                                    >
                                        <span className="rf-display text-7xl leading-[0.9] text-white sm:text-8xl">
                                            $299
                                        </span>
                                        <span className="mb-2 text-xl font-light text-white/55">
                                            /month
                                        </span>
                                    </div>
                                    <p className="mt-5 text-lg text-white/60">
                                        Simple. Fast. Ready to post.
                                    </p>
                                </div>

                                {/* Right: checklist — clean, scannable rows (no nested cards) */}
                                <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                                    {[...PRICE_LEFT, ...PRICE_RIGHT].map((b) => (
                                        <div
                                            key={b}
                                            className="flex items-center gap-3 border-b border-white/[0.06] py-2 last:border-0 sm:border-0 sm:py-1"
                                        >
                                            <CheckCircle2 className="h-5 w-5 flex-none text-purple-300" />
                                            <span className="text-base text-white/85">
                                                {b}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-10 flex justify-center">
                                <NeonButton
                                    size="block"
                                    data-testid="pricing-signup-button"
                                    onClick={() => openAuth("signup")}
                                    className="group max-w-2xl"
                                >
                                    Sign Up
                                    <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                                </NeonButton>
                            </div>
                        </div>
                    </AnimatedBorderCard>
                </motion.section>
            </main>

            <footer className="relative z-10 border-t border-white/10 py-8">
                <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-3 px-5 text-sm text-white/40 sm:flex-row sm:px-8">
                    <NeonLogo className="text-xl" />
                    <p>© {new Date().getFullYear()} ReelFlo. Your content, on autopilot.</p>
                </div>
            </footer>

            <AuthModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                mode={mode}
                setMode={setMode}
            />
        </div>
    );
}
